/**
 * Time Entries — global time tracking page.
 */
import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Skeleton,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Link,
  FormControlLabel,
  Switch,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerIcon from '@mui/icons-material/Timer';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import PaidOutlined from '@mui/icons-material/PaidOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { DataGrid, GridFooterContainer, GridPagination } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import api, { enhancedApi, mapApiErrorsToForm } from '../api';
import { runValidators } from '../utils/validators';
import { useFormValidation } from '../hooks/useFormValidation';
import { fieldHelperText } from '../utils/formHelpers';
import PageLayout from '../components/ui/PageLayout';
import { AuthContext } from '../components/AuthContext';
import { timeEntrySchema } from '../components/TimeEntryForm';

const formatElapsed = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
};

const formatDisplayDate = (val) => {
  if (!val) return '—';
  const d = dayjs(val);
  return d.isValid() ? d.format('MMM D, YYYY') : String(val);
};

const formatMoney = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const StatCard = ({ label, value, borderColor, icon }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      border: 1,
      borderColor: 'divider',
      borderLeftWidth: 4,
      borderLeftStyle: 'solid',
      borderLeftColor: borderColor,
      bgcolor: 'background.paper',
      height: '100%',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <Box sx={{ color: borderColor, display: 'flex', opacity: 0.9 }}>{icon}</Box>
    </Box>
    <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
      {value}
    </Typography>
  </Paper>
);

const billableFilterToParam = (v) => {
  if (v === 'billable') return true;
  if (v === 'nonbillable') return false;
  return undefined;
};

/** Dedupe default_hourly_rate fetches per client (module scope so effect always sees the same Set). */
const clientRatesRequestedIds = new Set();

const TimeTracking = () => {
  const navigate = useNavigate();
  const { user, userRole } = useContext(AuthContext) || {};
  const currentUserId = user?.id ?? parseInt(localStorage.getItem('userId'), 10);
  const isAdmin = userRole === 'admin' || userRole === 'all_access';

  const [dateFrom, setDateFrom] = useState(() => dayjs().startOf('week'));
  const [dateTo, setDateTo] = useState(() => dayjs().endOf('week'));
  const [clientFilter, setClientFilter] = useState(null);
  const [staffFilter, setStaffFilter] = useState(null);
  const [billableFilter, setBillableFilter] = useState('all');

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [clients, setClients] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);

  const [clientRates, setClientRates] = useState({});

  const [timerState, setTimerState] = useState(null);
  const [runningData, setRunningData] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timerLoading, setTimerLoading] = useState(true);
  const [timerError, setTimerError] = useState(null);
  const [startTimerOpen, setStartTimerOpen] = useState(false);
  const [startTimerClient, setStartTimerClient] = useState(null);
  const [startTimerLoading, setStartTimerLoading] = useState(false);

  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [stopForm, setStopForm] = useState({ description: '', billable: true });
  const [stopError, setStopError] = useState(null);
  const [stopLoading, setStopLoading] = useState(false);

  const [logOpen, setLogOpen] = useState(false);
  const [logForm, setLogForm] = useState({
    client: null,
    date: dayjs().format('YYYY-MM-DD'),
    hours: '1.00',
    description: '',
    billable: true,
  });
  const [logError, setLogError] = useState(null);
  const [logSaving, setLogSaving] = useState(false);

  const logFormValidation = useFormValidation(timeEntrySchema);
  const {
    errors: logE,
    touched: logT,
    validateField: logValField,
    touchField: logTouch,
    validateAll: logValAll,
    clearErrors: logClear,
    mergeServerFieldErrors: logMergeApi,
  } = logFormValidation;

  const [editOpen, setEditOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const editFormValidation = useFormValidation(timeEntrySchema);
  const {
    errors: editE,
    touched: editT,
    validateField: editValField,
    touchField: editTouch,
    validateAll: editValAll,
    clearErrors: editClear,
    mergeServerFieldErrors: editMergeApi,
  } = editFormValidation;

  const logFormValues = useMemo(
    () => ({
      client: String(logForm.client?.id ?? ''),
      hours: logForm.hours,
      description: logForm.description,
      date: logForm.date,
    }),
    [logForm]
  );

  const logAllValid = useMemo(
    () => !Object.keys(timeEntrySchema).some((k) => runValidators(logFormValues[k], timeEntrySchema[k])),
    [logFormValues]
  );

  const editFormValues = useMemo(
    () => ({
      client: String(editForm.client?.id ?? ''),
      hours: editForm.hours ?? '',
      description: editForm.description ?? '',
      date: editForm.date ?? '',
    }),
    [editForm]
  );

  const editAllValid = useMemo(
    () =>
      !Object.keys(timeEntrySchema).some((k) => runValidators(editFormValues[k], timeEntrySchema[k])),
    [editFormValues]
  );

  const fetchRunningTimer = useCallback(async () => {
    try {
      const res = await enhancedApi.getRunningTimer();
      const data = res.data;
      if (data?.running && data?.timer_started_at) {
        setTimerState('running');
        setRunningData({
          id: data.id,
          client_id: data.client_id,
          client_name: data.client_name || '',
          timer_started_at: data.timer_started_at,
        });
      } else {
        setTimerState('idle');
        setRunningData(null);
      }
      setTimerError(null);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      setTimerError(msg);
      setTimerState('idle');
      setRunningData(null);
    } finally {
      setTimerLoading(false);
    }
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        date_from: dateFrom.format('YYYY-MM-DD'),
        date_to: dateTo.format('YYYY-MM-DD'),
      };
      if (clientFilter?.id) params.client_id = clientFilter.id;
      if (staffFilter?.id) params.user_id = staffFilter.id;
      const billableParam = billableFilterToParam(billableFilter);
      if (billableParam !== undefined) params.billable = billableParam;
      const res = await enhancedApi.getTimeEntries(params);
      const list = res.data?.entries ?? (Array.isArray(res.data) ? res.data : []);
      setEntries(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to load time entries');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, clientFilter, staffFilter, billableFilter]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    fetchRunningTimer();
  }, [fetchRunningTimer]);

  useEffect(() => {
    if (timerState !== 'running' || !runningData?.timer_started_at) return;
    const tick = () => {
      const started = new Date(runningData.timer_started_at).getTime();
      setElapsedSeconds((Date.now() - started) / 1000);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [timerState, runningData?.timer_started_at]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await enhancedApi.getClients();
        setClients(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setClients([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await enhancedApi.getUsers();
        const userList = Array.isArray(res.data) ? res.data : res.data?.users || res.data?.data || [];
        const opts = userList.map((u) => ({
          id: u.id,
          label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email || `User ${u.id}`,
        }));
        if (!cancelled) setStaffOptions(opts);
      } catch {
        if (!cancelled) setStaffOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const raw = entries.filter((e) => e.client_id && !(e.timer_started_at && parseFloat(e.hours) === 0));
    const ids = [...new Set(raw.map((e) => e.client_id))];
    const missing = ids.filter((id) => !clientRatesRequestedIds.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => clientRatesRequestedIds.add(id));

    let cancelled = false;
    (async () => {
      const updates = {};
      await Promise.all(
        missing.map(async (cid) => {
          try {
            const res = await api.get(`/api/multi-tenant/clients/${cid}/`);
            const r = res.data?.default_hourly_rate;
            updates[cid] = r != null && r !== '' ? parseFloat(String(r)) : null;
          } catch {
            updates[cid] = null;
          }
        })
      );
      if (!cancelled) {
        setClientRates((prev) => {
          const next = { ...prev };
          Object.entries(updates).forEach(([k, v]) => {
            next[Number(k)] = v;
          });
          return next;
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entries]);

  const clientOptions = useMemo(
    () =>
      clients.map((c) => ({
        id: c.id,
        label: c.display_name || c.name || `Client ${c.id}`,
      })),
    [clients]
  );

  const displayEntries = useMemo(
    () => entries.filter((e) => !(e.timer_started_at && parseFloat(e.hours) === 0)),
    [entries]
  );

  const rows = useMemo(
    () =>
      displayEntries.map((e) => {
        const rate = e.client_id != null ? clientRates[e.client_id] : null;
        const h = parseFloat(e.hours) || 0;
        const amount = e.billable && rate != null && !Number.isNaN(rate) ? h * rate : null;
        return { ...e, computedRate: rate, computedAmount: amount };
      }),
    [displayEntries, clientRates]
  );

  const { totalHours, billableHoursSum, totalValue } = useMemo(() => {
    let th = 0;
    let bh = 0;
    let tv = 0;
    displayEntries.forEach((e) => {
      const h = parseFloat(e.hours) || 0;
      th += h;
      if (e.billable) bh += h;
      const rate = e.client_id != null ? clientRates[e.client_id] : null;
      if (e.billable && rate != null && !Number.isNaN(rate)) tv += h * rate;
    });
    return { totalHours: th, billableHoursSum: bh, totalValue: tv };
  }, [displayEntries, clientRates]);

  const footerTotals = useMemo(() => {
    let th = 0;
    let bh = 0;
    let tv = 0;
    rows.forEach((e) => {
      const h = parseFloat(e.hours) || 0;
      th += h;
      if (e.billable) bh += h;
      if (e.computedAmount != null) tv += e.computedAmount;
    });
    return { th, bh, tv };
  }, [rows]);

  const canEditEntry = useCallback(
    (entry) => {
      if (!entry || entry.invoice_id) return false;
      if (isAdmin) return true;
      return String(entry.user_id) === String(currentUserId);
    },
    [isAdmin, currentUserId]
  );

  const handleOptionSelect = (option) => {
    if (option === 'home') navigate('/dashboard');
    else if (option === 'workflows') navigate('/workflows');
  };

  const openLogTime = () => {
    logClear();
    setLogForm({
      client: clientFilter || null,
      date: dayjs().format('YYYY-MM-DD'),
      hours: '1.00',
      description: '',
      billable: true,
    });
    setLogError(null);
    setLogOpen(true);
  };

  const submitLogTime = async () => {
    const lv = {
      client: String(logForm.client?.id ?? ''),
      hours: logForm.hours,
      description: logForm.description,
      date: logForm.date,
    };
    if (!logValAll(lv)) return;
    let hoursVal = parseFloat(logForm.hours);
    if (Number.isNaN(hoursVal)) hoursVal = 0;
    setLogSaving(true);
    setLogError(null);
    try {
      await enhancedApi.createTimeEntry({
        client_id: logForm.client.id,
        date: logForm.date,
        hours: hoursVal,
        description: (logForm.description || '').trim(),
        billable: logForm.billable,
      });
      logClear();
      setLogOpen(false);
      fetchEntries();
    } catch (err) {
      const mapped = mapApiErrorsToForm(err.response?.data);
      const m = { ...mapped };
      if (m.client_id) m.client = m.client_id;
      if (Object.keys(m).length) logMergeApi(m);
      setLogError(err.response?.data?.error || err.response?.data?.detail || err.message);
    } finally {
      setLogSaving(false);
    }
  };

  const openStartTimerDialog = () => {
    setStartTimerClient(clientFilter || null);
    setStartTimerOpen(true);
    setTimerError(null);
  };

  const submitStartTimer = async () => {
    setStartTimerLoading(true);
    setTimerError(null);
    try {
      await enhancedApi.startTimer({ client_id: startTimerClient?.id ?? null });
      setStartTimerOpen(false);
      await fetchRunningTimer();
      fetchEntries();
    } catch (err) {
      setTimerError(err.response?.data?.error || err.response?.data?.detail || err.message);
    } finally {
      setStartTimerLoading(false);
    }
  };

  const openStopModal = () => {
    setStopForm({ description: '', billable: true });
    setStopError(null);
    setStopModalOpen(true);
  };

  const submitStopTimer = async () => {
    const desc = (stopForm.description || '').trim();
    if (desc.length < 3) {
      setStopError('Description must be at least 3 characters.');
      return;
    }
    setStopLoading(true);
    setStopError(null);
    try {
      await enhancedApi.stopTimer({ description: desc, billable: stopForm.billable });
      setStopModalOpen(false);
      await fetchRunningTimer();
      fetchEntries();
    } catch (err) {
      setStopError(err.response?.data?.error || err.response?.data?.detail || err.message);
    } finally {
      setStopLoading(false);
    }
  };

  const openEdit = useCallback(
    (entry) => {
      if (!canEditEntry(entry)) return;
      const co = clientOptions.find((c) => c.id === entry.client_id) || {
        id: entry.client_id,
        label: entry.client_name || `Client ${entry.client_id}`,
      };
      setEditEntry(entry);
      setEditForm({
        client: entry.client_id ? co : null,
        date: entry.date ? String(entry.date).slice(0, 10) : dayjs().format('YYYY-MM-DD'),
        hours: String(entry.hours),
        description: entry.description || '',
        billable: !!entry.billable,
      });
      setEditError(null);
      setEditOpen(true);
    },
    [canEditEntry, clientOptions]
  );

  const submitEdit = async () => {
    if (!editEntry) return;
    const ev = {
      client: String(editForm.client?.id ?? ''),
      hours: editForm.hours ?? '',
      description: editForm.description ?? '',
      date: editForm.date ?? '',
    };
    if (!editValAll(ev)) return;
    let hoursVal = parseFloat(editForm.hours);
    if (Number.isNaN(hoursVal)) hoursVal = 0;
    setEditSaving(true);
    setEditError(null);
    try {
      await enhancedApi.updateTimeEntry(editEntry.id, {
        hours: hoursVal,
        description: (editForm.description || '').trim(),
        billable: editForm.billable,
        date: editForm.date,
        client_id: editForm.client?.id ?? null,
      });
      editClear();
      setEditOpen(false);
      setEditEntry(null);
      fetchEntries();
    } catch (err) {
      const mapped = mapApiErrorsToForm(err.response?.data);
      const m = { ...mapped };
      if (m.client_id) m.client = m.client_id;
      if (Object.keys(m).length) editMergeApi(m);
      setEditError(err.response?.data?.error || err.response?.data?.detail || err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = useCallback(
    async (entry) => {
      if (!canEditEntry(entry)) return;
      if (!window.confirm('Delete this time entry?')) return;
      try {
        await enhancedApi.deleteTimeEntry(entry.id);
        fetchEntries();
      } catch (err) {
        setError(err.response?.data?.error || err.response?.data?.detail || err.message);
      }
    },
    [canEditEntry, fetchEntries]
  );

  const columns = useMemo(
    () => [
      {
        field: 'date',
        headerName: 'Date',
        flex: 0.9,
        minWidth: 110,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {formatDisplayDate(params.value)}
          </Typography>
        ),
      },
      {
        field: 'client_name',
        headerName: 'Client',
        flex: 1.1,
        minWidth: 140,
        renderCell: (params) => {
          const cid = params.row.client_id;
          const name = params.value || '—';
          if (!cid) return <Typography variant="body2">{name}</Typography>;
          return (
            <Link component={RouterLink} to={`/client/${cid}`} variant="body2" fontWeight={500} underline="hover">
              {name}
            </Link>
          );
        },
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1.6,
        minWidth: 160,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {params.value || '—'}
          </Typography>
        ),
      },
      {
        field: 'hours',
        headerName: 'Hours',
        flex: 0.55,
        minWidth: 80,
        type: 'number',
        renderCell: (params) => (
          <Typography variant="body2">{(parseFloat(params.value) || 0).toFixed(2)}</Typography>
        ),
      },
      {
        field: 'billable',
        headerName: 'Billable',
        flex: 0.75,
        minWidth: 100,
        renderCell: (params) =>
          params.value ? (
            <Chip size="small" label="Billable" color="success" />
          ) : (
            <Chip size="small" label="Non-Billable" variant="filled" sx={{ bgcolor: 'grey.400', color: 'grey.900' }} />
          ),
      },
      {
        field: 'computedRate',
        headerName: 'Rate',
        flex: 0.65,
        minWidth: 88,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.value != null && !Number.isNaN(params.value) ? formatMoney(params.value) : '—'}
          </Typography>
        ),
      },
      {
        field: 'computedAmount',
        headerName: 'Amount',
        flex: 0.75,
        minWidth: 96,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{
              width: '100%',
              textAlign: 'right',
              color: params.value != null ? 'text.primary' : 'text.disabled',
            }}
          >
            {params.value != null ? formatMoney(params.value) : '—'}
          </Typography>
        ),
      },
      {
        field: 'user_name',
        headerName: 'Staff',
        flex: 0.95,
        minWidth: 120,
        renderCell: (params) => <Typography variant="body2">{params.value || '—'}</Typography>,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0.55,
        minWidth: 88,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const entry = params.row;
          const allow = canEditEntry(entry);
          return (
            <Box
              className="time-entries-row-actions"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                height: '100%',
                opacity: 0,
                transition: 'opacity 0.12s ease-out',
                '.MuiDataGrid-row:hover &': { opacity: 1 },
              }}
            >
              <Tooltip title="Edit">
                <span>
                  <IconButton size="small" disabled={!allow} onClick={() => openEdit(entry)} aria-label="Edit entry">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete">
                <span>
                  <IconButton size="small" disabled={!allow} onClick={() => handleDelete(entry)} aria-label="Delete entry">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [canEditEntry, openEdit, handleDelete]
  );

  const CustomFooter = useCallback(
    () => (
      <GridFooterContainer
        sx={{
          flexDirection: 'column',
          alignItems: 'stretch',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            px: 2,
            py: 1.25,
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="body2" fontWeight={700}>
            Totals
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'baseline' }}>
            <Typography variant="body2" fontWeight={700}>
              {footerTotals.th.toFixed(2)} hrs
            </Typography>
            <Typography variant="body2" fontWeight={700} color="success.main">
              {footerTotals.bh.toFixed(2)} billable hrs
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ textAlign: 'right' }}>
              {formatMoney(footerTotals.tv)}
            </Typography>
          </Box>
        </Box>
        <GridPagination />
      </GridFooterContainer>
    ),
    [footerTotals]
  );

  const showEmpty = !loading && rows.length === 0;

  return (
    <PageLayout onOptionSelect={handleOptionSelect}>
      <Box sx={{ width: '100%', maxWidth: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
            Time Entries
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
            {timerState === 'running' && runningData && (
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    flexShrink: 0,
                    animation: 'timeEntryPulse 1.2s ease-in-out infinite',
                    '@keyframes timeEntryPulse': {
                      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.45, transform: 'scale(0.85)' },
                    },
                  }}
                />
                <Typography component="span" sx={{ fontFamily: 'monospace', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {formatElapsed(elapsedSeconds)}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                  {runningData.client_name || 'No client'}
                </Typography>
                <Button variant="outlined" color="error" size="small" onClick={openStopModal}>
                  Stop
                </Button>
              </Paper>
            )}
            {timerLoading ? <CircularProgress size={22} /> : null}
            {timerError && (
              <Alert severity="error" sx={{ py: 0 }} onClose={() => setTimerError(null)}>
                {timerError}
              </Alert>
            )}
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openLogTime}>
              Log Time
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<TimerIcon />}
              onClick={openStartTimerDialog}
              disabled={timerState === 'running' || timerLoading}
            >
              Start Timer
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <StatCard label="Total Hours" value={totalHours.toFixed(2)} borderColor="primary.main" icon={<ScheduleRounded fontSize="small" />} />
          <StatCard label="Billable Hours" value={billableHoursSum.toFixed(2)} borderColor="success.main" icon={<PaidOutlined fontSize="small" />} />
          <StatCard label="Total Value" value={formatMoney(totalValue)} borderColor="warning.main" icon={<WarningAmberOutlined fontSize="small" />} />
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <DatePicker
                label="From"
                value={dateFrom}
                onChange={(v) => v && setDateFrom(v.startOf('day'))}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
              />
              <DatePicker
                label="To"
                value={dateTo}
                onChange={(v) => v && setDateTo(v.endOf('day'))}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
              />
              <Autocomplete
                size="small"
                options={clientOptions}
                value={clientFilter}
                onChange={(_, v) => setClientFilter(v)}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                sx={{ minWidth: 220, flex: { xs: '1 1 100%', sm: '0 1 220px' } }}
                renderInput={(params) => <TextField {...params} label="Client" placeholder="All clients" />}
              />
              <Autocomplete
                size="small"
                options={staffOptions}
                value={staffFilter}
                onChange={(_, v) => setStaffFilter(v)}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '0 1 200px' } }}
                renderInput={(params) => <TextField {...params} label="Staff" placeholder="All staff" />}
              />
              <ToggleButtonGroup
                exclusive
                size="small"
                value={billableFilter}
                onChange={(_, v) => v != null && setBillableFilter(v)}
                sx={{ flexWrap: 'wrap' }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="billable">Billable</ToggleButton>
                <ToggleButton value="nonbillable">Non-Billable</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </LocalizationProvider>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
            <Skeleton variant="rectangular" height={48} />
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Skeleton key={i} variant="rectangular" height={52} sx={{ mt: 0.5 }} />
            ))}
          </Paper>
        ) : showEmpty ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              py: 8,
              px: 3,
              textAlign: 'center',
              bgcolor: 'background.paper',
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No time entries
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openLogTime} sx={{ mt: 1 }}>
              Log your first entry
            </Button>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              overflow: 'hidden',
              '& .MuiDataGrid-columnHeaders': { bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' },
              '& .MuiDataGrid-cell': { py: 1, alignItems: 'center', display: 'flex' },
              '& .MuiDataGrid-row:not(.MuiDataGrid-row--selected):hover': { bgcolor: 'action.hover' },
              '& .MuiDataGrid-footerContainer': { borderTop: 1, borderColor: 'divider' },
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(r) => r.id}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ footer: CustomFooter }}
              sx={{ border: 'none' }}
            />
          </Paper>
        )}
      </Box>

      <Dialog open={startTimerOpen} onClose={() => !startTimerLoading && setStartTimerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Start timer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Optionally associate this session with a client.
          </Typography>
          <Autocomplete
            options={clientOptions}
            value={startTimerClient}
            onChange={(_, v) => setStartTimerClient(v)}
            getOptionLabel={(o) => o?.label || ''}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            renderInput={(params) => <TextField {...params} label="Client (optional)" margin="normal" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartTimerOpen(false)} disabled={startTimerLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitStartTimer} disabled={startTimerLoading}>
            {startTimerLoading ? 'Starting…' : 'Start'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={stopModalOpen} onClose={() => !stopLoading && setStopModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Stop timer</DialogTitle>
        <DialogContent>
          {stopError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStopError(null)}>
              {stopError}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            placeholder="What did you work on?"
            value={stopForm.description}
            onChange={(e) => setStopForm((f) => ({ ...f, description: e.target.value }))}
            required
            helperText="Minimum 3 characters"
            sx={{ mt: 1, mb: 2 }}
          />
          <FormControlLabel
            control={<Switch checked={stopForm.billable} onChange={(e) => setStopForm((f) => ({ ...f, billable: e.target.checked }))} />}
            label="Billable"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopModalOpen(false)} disabled={stopLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitStopTimer} disabled={stopLoading || (stopForm.description || '').trim().length < 3}>
            {stopLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={logOpen} onClose={() => !logSaving && setLogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log time</DialogTitle>
        <DialogContent>
          {logError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLogError(null)}>
              {logError}
            </Alert>
          )}
          <Autocomplete
            options={clientOptions}
            value={logForm.client}
            onChange={(_, v) => {
              setLogForm((f) => ({ ...f, client: v }));
              if (logT.client) logValField('client', String(v?.id ?? ''));
            }}
            getOptionLabel={(o) => o?.label || ''}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client"
                margin="normal"
                required
                error={Boolean(logT.client && logE.client)}
                helperText={logT.client && logE.client ? logE.client : undefined}
                onBlur={() => logTouch('client', String(logForm.client?.id ?? ''))}
              />
            )}
          />
          <TextField
            fullWidth
            type="date"
            label="Date"
            value={logForm.date}
            onChange={(e) => {
              const v = e.target.value;
              setLogForm((f) => ({ ...f, date: v }));
              if (logT.date) logValField('date', v);
            }}
            onBlur={() => logTouch('date', logForm.date)}
            error={Boolean(logT.date && logE.date)}
            helperText={logT.date && logE.date ? logE.date : undefined}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Hours"
            value={logForm.hours}
            onChange={(e) => {
              const v = e.target.value;
              setLogForm((f) => ({ ...f, hours: v }));
              if (logT.hours) logValField('hours', v);
            }}
            onBlur={() => logTouch('hours', logForm.hours)}
            error={Boolean(logT.hours && logE.hours)}
            helperText={logT.hours && logE.hours ? logE.hours : undefined}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={logForm.description}
            onChange={(e) => {
              const v = e.target.value.slice(0, 500);
              setLogForm((f) => ({ ...f, description: v }));
              if (logT.description) logValField('description', v);
            }}
            onBlur={() => logTouch('description', logForm.description)}
            error={Boolean(logT.description && logE.description)}
            helperText={fieldHelperText(
              logT.description && logE.description,
              500,
              (logForm.description || '').length
            )}
            margin="normal"
            multiline
            rows={3}
            inputProps={{ maxLength: 500 }}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch checked={logForm.billable} onChange={(e) => setLogForm((f) => ({ ...f, billable: e.target.checked }))} />}
            label="Billable"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogOpen(false)} disabled={logSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitLogTime} disabled={logSaving || !logAllValid}>
            {logSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => !editSaving && setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit time entry</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError(null)}>
              {editError}
            </Alert>
          )}
          <Autocomplete
            options={clientOptions}
            value={editForm.client}
            onChange={(_, v) => {
              setEditForm((f) => ({ ...f, client: v }));
              if (editT.client) editValField('client', String(v?.id ?? ''));
            }}
            getOptionLabel={(o) => o?.label || ''}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client"
                margin="normal"
                required
                error={Boolean(editT.client && editE.client)}
                helperText={editT.client && editE.client ? editE.client : undefined}
                onBlur={() => editTouch('client', String(editForm.client?.id ?? ''))}
              />
            )}
          />
          <TextField
            fullWidth
            type="date"
            label="Date"
            value={editForm.date}
            onChange={(e) => {
              const v = e.target.value;
              setEditForm((f) => ({ ...f, date: v }));
              if (editT.date) editValField('date', v);
            }}
            onBlur={() => editTouch('date', editForm.date)}
            error={Boolean(editT.date && editE.date)}
            helperText={editT.date && editE.date ? editE.date : undefined}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Hours"
            value={editForm.hours}
            onChange={(e) => {
              const v = e.target.value;
              setEditForm((f) => ({ ...f, hours: v }));
              if (editT.hours) editValField('hours', v);
            }}
            onBlur={() => editTouch('hours', editForm.hours)}
            error={Boolean(editT.hours && editE.hours)}
            helperText={editT.hours && editE.hours ? editE.hours : undefined}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={editForm.description}
            onChange={(e) => {
              const v = e.target.value.slice(0, 500);
              setEditForm((f) => ({ ...f, description: v }));
              if (editT.description) editValField('description', v);
            }}
            onBlur={() => editTouch('description', editForm.description)}
            error={Boolean(editT.description && editE.description)}
            helperText={fieldHelperText(
              editT.description && editE.description,
              500,
              (editForm.description || '').length
            )}
            margin="normal"
            multiline
            rows={3}
            inputProps={{ maxLength: 500 }}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch checked={editForm.billable} onChange={(e) => setEditForm((f) => ({ ...f, billable: e.target.checked }))} />}
            label="Billable"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitEdit} disabled={editSaving || !editAllValid}>
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default TimeTracking;
